import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import hero_img from './../../../assets/images/hero_img.jpg';

@customElement('rftw-hero')
export class RftwHero extends LitElement {
  /** Tag line above the main title */
  @property({ type: String }) editionTag = 'EDITION 01 · NOSARA, GUANACASTE';

  /** Main headline */
  @property({ type: String }) title =
    'De ride por Costa Rica. Protege la vida silvestre.';

  /** Subheading / main paragraph description */
  @property({ type: String }) description =
    'Ride for the Wild es una serie de rides off-road por todo el país. Cada edición recorre una región distinta y financia una causa distinta de fauna silvestre.';

  /** Highlight note above action buttons */
  @property({ type: String }) highlightText =
    'Primera edición por Guanacaste a favor de IAR Nosara.';

  static styles = css`
    :host {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    /* Container layout using background overlay + background image */
    .hero-container {
      position: relative;
      width: 100%;
      min-height: 520px;
      display: flex;
      align-items: center;
      background-color: #1c1d1a;
      background-image:
        linear-gradient(
          160deg,
          rgba(255, 255, 255, 0.52) 0%,
          rgba(186, 183, 174, 0.34) 36%,
          rgba(33, 33, 29, 0.46) 70%,
          rgba(12, 13, 11, 0.92) 100%
        ),
        var(--hero-bg-url);
      background-size: cover, contain;
      background-position:
        center,
        right max(2rem, calc((100vw - 1200px) / 2)) center;
      background-repeat: no-repeat;
      padding: 4rem 2rem;
      box-sizing: border-box;
      overflow: hidden;
    }

    .hero-content {
      max-width: 620px;
      margin-left: max(0px, calc((100vw - 1200px) / 2 - 2rem));
      z-index: 2;
    }

    .edition-tag {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #ffffff;
      margin-bottom: 0.75rem;
    }

    .hero-title {
      font-size: clamp(2.2rem, 5vw, 3.4rem);
      font-weight: 800;
      line-height: 1.1;
      color: #ffffff;
      margin: 0 0 1.25rem 0;
      letter-spacing: -0.02em;
    }

    .hero-description {
      font-size: 1rem;
      line-height: 1.6;
      color: #d0d5dd;
      margin: 0 0 1.5rem 0;
      max-width: 540px;
    }

    .hero-highlight {
      font-size: 1rem;
      font-weight: 700;
      color: #ffffff;
      margin-bottom: 2rem;
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
    }

    /* Buttons */
    .btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.8rem 1.6rem;
      font-size: 0.95rem;
      font-weight: 600;
      border-radius: 6px;
      cursor: pointer;
      text-decoration: none;
      transition: all 0.2s ease-in-out;
      border: 1px solid transparent;
      box-sizing: border-box;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .btn-primary {
      background-color: var(--wa-color-brand, #d95328);
      color: var(--wa-color-brand-on, #ffffff);
    }

    .btn-primary:hover,
    .btn-primary:focus-visible {
      background-color: var(--wa-color-brand-70, #be431d);
      outline: none;
    }

    .btn-outline {
      background-color: rgba(45, 43, 33, 0.42);
      color: #ffffff;
      border-color: #8b8244;
      box-shadow: inset 0 0 0 1px rgba(255, 245, 172, 0.12);
    }

    .btn-outline:hover,
    .btn-outline:focus-visible {
      background-color: rgba(74, 69, 44, 0.68);
      border-color: #b6aa5a;
      outline: none;
    }

    /* Mobile Responsive Adjustments */
    @media (max-width: 768px) {
      .hero-container {
        min-height: 34rem;
        padding: 3.5rem 1.25rem 2.5rem;
        background-image:
          linear-gradient(
            180deg,
            rgba(29, 30, 27, 0.55) 0%,
            rgba(18, 19, 17, 0.78) 56%,
            rgba(10, 11, 10, 0.94) 100%
          ),
          var(--hero-bg-url);
        background-size: cover, contain;
        background-position: center, center;
      }

      .actions {
        flex-direction: column;
        align-items: stretch;
      }

      .btn {
        min-height: 3rem;
        width: 100%;
      }
    }
  `;

  private _handleRegisterClick(e: Event) {
    e.preventDefault();
    /** Dispatches custom event for parent component / router handling */
    this.dispatchEvent(
      new CustomEvent('register-click', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private _handleGpxClick(e: Event) {
    e.preventDefault();
    /** Dispatches custom event for parent component / GPX file download handling */
    this.dispatchEvent(
      new CustomEvent('gpx-click', {
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    return html`
      <section
        class="hero-container"
        style="--hero-bg-url: url('${hero_img}')"
        aria-label="Hero Section"
      >
        <div class="hero-content">
          ${this.editionTag
            ? html`<div class="edition-tag">${this.editionTag}</div>`
            : ''}

          <h1 class="hero-title">${this.title}</h1>

          <p class="hero-description">${this.description}</p>

          ${this.highlightText
            ? html`<div class="hero-highlight">${this.highlightText}</div>`
            : ''}

          <div class="actions">
            <button class="btn btn-primary" @click=${this._handleRegisterClick}>
              Inscribirme al ride
            </button>
            <!-- <button class="btn btn-outline" @click=${this._handleGpxClick}>
              Ver la ruta GPX
            </button> -->
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rftw-hero': RftwHero;
  }
}

